import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useToast } from 'components/hooks/useToast';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { QUERY_KEYS } from './index';

const getCustomTagsApi = () => {
  const url = endpoints.rabbitGoals.getCustomTagsOrganization;
  return sendRequest({
    method: 'GET',
    url,
    credentials: 'include',
  });
};

export const useCustomTagsQuery = () =>
  useQuery([QUERY_KEYS.CUSTOM_TAGS], () => getCustomTagsApi().then((res) => res?.entity || []), { retry: 1 });

const getOKRTagsApi = ({ okrId }) => {
  const url = endpoints.rabbitGoals.getOKRTags(okrId);
  return sendRequest({
    method: 'GET',
    url,
    credentials: 'include',
  });
};

export const useOKRTagsQuery = ({ okrId }) =>
  useQuery([QUERY_KEYS.OKR_CUSTOM_TAGS, okrId], () => getOKRTagsApi({ okrId }).then((res) => res?.entity || []), {
    retry: 1,
    enabled: Boolean(okrId),
  });

const updateCustomTagsApi = ({ tagName = '', uuid = null }) => {
  const url = endpoints.rabbitGoals.createCustomTagsOrganization;
  return sendRequest({
    method: 'POST',
    url,
    body: { tagName, uuid },
    credentials: 'include',
  });
};

export const useUpdateCustomTagsMutation = (onUpdate = () => {}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagDetails }) => updateCustomTagsApi(tagDetails),
    onError: (err) => {
      toast({
        title: err.message || 'failed to create tag',
        status: TOAST_STATUSES.ERROR,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_TAGS]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsInsightsGroupByGoalsData]);
      setTimeout(() => {
        queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_COMMENTS]);
      }, 1000);
      if (variables?.okrId) {
        queryClient.invalidateQueries([QUERY_KEYS.OKR_CUSTOM_TAGS, variables?.okrId]);
      }

      if (typeof onUpdate === 'function') {
        onUpdate(data?.entity);
      }
      toast({
        title: data?.message || 'Tag Updated Successfully',
        status: TOAST_STATUSES.SUCCESS,
      });
    },
  });
};

const attachTagToOKRApi = ({ okrId = null, tagKeyId }) => {
  const url = endpoints.rabbitGoals.attachTagToOKR(okrId);
  return sendRequest({
    method: 'PUT',
    url,
    body: { tagKeyId },
    credentials: 'include',
  });
};

export const useAttachTagToOKRMutation = (onUpdate = () => {}, onError = () => {}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => attachTagToOKRApi(payload),
    onError: (err) => {
      if (typeof onError === 'function') {
        onError();
      }
      toast({
        title: err.message || 'Failed to attach the tag',
        status: TOAST_STATUSES.ERROR,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_TAGS]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsInsightsGroupByGoalsData]);
      queryClient.invalidateQueries([QUERY_KEYS.OKR_CUSTOM_TAGS, variables?.okrId]);
      setTimeout(() => {
        queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_COMMENTS]);
      }, 1000);
      if (typeof onUpdate === 'function') {
        onUpdate();
      }
      toast({
        title: data?.message || 'Tags attached Successfully',
        status: TOAST_STATUSES.SUCCESS,
      });
    },
  });
};

const modifyTagToOKRApi = ({ okrId = null, tagKeyId }) => {
  const url = endpoints.rabbitGoals.modifyTagToOKR(okrId);
  return sendRequest({
    method: 'POST',
    url,
    body: { tagKeyId },
    credentials: 'include',
  });
};

export const useModifyTagToOKRMutation = (onUpdate = () => {}, onError = () => {}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => modifyTagToOKRApi(payload),
    onError: (err) => {
      if (typeof onError === 'function') {
        onError();
      }
      toast({
        title: err.message || 'Failed to delete the tag',
        status: TOAST_STATUSES.ERROR,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_TAGS]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsInsightsGroupByGoalsData]);
      queryClient.invalidateQueries([QUERY_KEYS.OKR_CUSTOM_TAGS, variables?.okrId]);
      setTimeout(() => {
        queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_COMMENTS]);
      }, 1000);

      if (typeof onUpdate === 'function') {
        onUpdate();
      }
      // queryClient.setQueryData(queryKey, data?.entity);
      toast({
        title: data?.message || 'Tag removed Successfully',
        status: TOAST_STATUSES.SUCCESS,
      });
    },
  });
};

const deleteOrganizationTagApi = ({ tagKeyId }) => {
  const url = endpoints.rabbitGoals.deleteOrganizationTag(tagKeyId);
  return sendRequest({
    method: 'DELETE',
    url,
    credentials: 'include',
  });
};

export const useDeleteOrganizationTagMutation = (onUpdate = () => {}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => deleteOrganizationTagApi(payload),
    onError: (err) => {
      toast({
        title: err.message || 'Failed to delete the tag',
        status: TOAST_STATUSES.ERROR,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_TAGS]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsInsightsGroupByGoalsData]);
      setTimeout(() => {
        queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_COMMENTS]);
      }, 1000);
      if (variables?.okrId) {
        queryClient.invalidateQueries([QUERY_KEYS.OKR_CUSTOM_TAGS, variables?.okrId]);
      }

      if (typeof onUpdate === 'function') {
        onUpdate(variables.tagKeyId);
      }
      toast({
        title: data?.message || 'Tag deleted Successfully',
        status: TOAST_STATUSES.SUCCESS,
      });
    },
  });
};
